import React, { useState, useMemo } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { CreditCard } from 'lucide-react';
import StripeCardForm from './StripeCardForm';
import PaymentNoticeModal from './PaymentNoticeModal';
import { paymentsAPI } from '../../api/payments';
import { useCurrencyStore } from '../../store/currencyStore';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';

interface ContinueOrderPaymentProps {
  orderId: string;
  orderTotalUsd: number;
}

const STRIPE_UNSUPPORTED_CURRENCIES = ['GHS'];

const ContinueOrderPayment: React.FC<ContinueOrderPaymentProps> = ({ orderId, orderTotalUsd }) => {
  const { selectedCurrency, format, exchangeRates } = useCurrencyStore();
  const { user } = useAuthStore();
  const [paymentMethod, setPaymentMethod] = useState<string>('card');
  const [isLoading, setIsLoading] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [stripePublishableKey, setStripePublishableKey] = useState<string | null>(null);
  const [paymentNotice, setPaymentNotice] = useState<{ title: string; message: string } | null>(null);

  const stripePromise = useMemo<Promise<Stripe | null> | null>(
    () => (stripePublishableKey ? loadStripe(stripePublishableKey) : null),
    [stripePublishableKey]
  );

  const isNgn = selectedCurrency.code === 'NGN';
  const getStripeCurrency = () =>
    STRIPE_UNSUPPORTED_CURRENCIES.includes(selectedCurrency.code) ? 'USD' : selectedCurrency.code;

  const convertToChargeCurrency = (amountUsd: number, toCurrency: string) => {
    if (toCurrency === 'USD') return amountUsd;
    const rate = exchangeRates[toCurrency] || 1;
    return amountUsd * rate;
  };

  const handleSelectCard = async () => {
    setPaymentMethod('card');
    if (isNgn || stripeClientSecret) return;

    try {
      setIsLoading(true);
      const stripeCurrency = getStripeCurrency();
      const amount = convertToChargeCurrency(orderTotalUsd, stripeCurrency);
      const res = await paymentsAPI.initializeStripe({
        order_id: orderId,
        amount,
        currency: stripeCurrency,
      });
      if (res.success && res.data) {
        setStripeClientSecret(res.data.client_secret);
        setStripePublishableKey(res.data.publishable_key);
      } else {
        setPaymentNotice({ title: "Card Payment Unavailable", message: res.message || "We were unable to set up card payment right now. Please try again shortly." });
      }
    } catch (err: any) {
      setPaymentNotice({ title: "Card Payment Unavailable", message: err.response?.data?.message || "We were unable to set up card payment right now. Please try again shortly." });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayNgnOrPaystack = async (cardOnly: boolean) => {
    try {
      setIsLoading(true);
      const amount = convertToChargeCurrency(orderTotalUsd, 'NGN');
      const res = await paymentsAPI.initializePaystack({
        order_id: orderId,
        email: user?.email || '',
        amount,
        currency: 'NGN',
        card_only: cardOnly,
      });
      if (res.success && res.data?.authorization_url) {
        window.location.href = res.data.authorization_url;
      } else {
        setPaymentNotice({ title: "Payment Could Not Be Started", message: res.message || "We were unable to start your Paystack payment. Please try again shortly." });
      }
    } catch {
      setPaymentNotice({ title: "Payment Could Not Be Started", message: "We were unable to start your Paystack payment. Please try again shortly." });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayPal = async () => {
    try {
      setIsLoading(true);
      const PAYPAL_SUPPORTED_CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF'];
      const paypalCurrency = PAYPAL_SUPPORTED_CURRENCIES.includes(selectedCurrency.code)
        ? selectedCurrency.code
        : 'USD';
      const amount = convertToChargeCurrency(orderTotalUsd, paypalCurrency);
      const res = await paymentsAPI.initializePaypal({
        order_id: orderId,
        amount,
        currency: paypalCurrency,
      });
      if (res.success && res.data?.approve_url) {
        window.location.href = res.data.approve_url;
      } else {
        setPaymentNotice({ title: "Payment Could Not Be Started", message: res.message || "We were unable to start your PayPal payment. Please try again shortly." });
      }
    } catch {
      setPaymentNotice({ title: "Payment Could Not Be Started", message: "We were unable to start your PayPal payment. Please try again shortly." });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-light p-6">
      <h2 className="font-serif text-lg text-primary mb-4">Continue Payment</h2>
      <div className="space-y-3">
        <button
          onClick={isNgn ? () => handlePayNgnOrPaystack(true) : handleSelectCard}
          disabled={isLoading}
          className={`w-full text-left p-3 border bg-white transition-colors disabled:opacity-50 ${
            paymentMethod === 'card' ? 'border-primary' : 'border-light hover:border-nude'
          }`}
        >
          <div className="flex items-center space-x-2">
            <CreditCard size={16} className="text-nude" />
            <span className="text-xs font-medium tracking-widest uppercase">Pay with Card</span>
          </div>
        </button>

        {paymentMethod === 'card' && !isNgn && stripeClientSecret && stripePromise && (
          <Elements stripe={stripePromise} options={{ clientSecret: stripeClientSecret }}>
            <StripeCardForm
              orderId={orderId}
              totalLabel={format(orderTotalUsd)}
              onSuccess={() => window.location.reload()}
            />
          </Elements>
        )}

        {isNgn && (
          <button
            onClick={() => handlePayNgnOrPaystack(false)}
            disabled={isLoading}
            className="w-full text-left p-3 border border-light bg-white hover:border-nude transition-colors disabled:opacity-50"
          >
            <span className="text-xs font-medium tracking-widest uppercase">Paystack</span>
          </button>
        )}
      </div>

      {paymentNotice && (
        <PaymentNoticeModal
          title={paymentNotice.title}
          message={paymentNotice.message}
          onClose={() => setPaymentNotice(null)}
        />
      )}
    </div>
  );
};

export default ContinueOrderPayment;